// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessLeads.jsx
// Название: Business Leads Management v5.0
// Версия: 1.0.0
// Дата: 21.01.2026

import React, { useState, useEffect } from 'react';
import './BusinessLeads.css';

const API_URL = 'https://qr-money.onrender.com/api';

const BusinessLeads = () => {
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ campaign_id: '', type: '', search: '' });
  const [stats, setStats] = useState({ total: 0, direct: 0, referral: 0 });

  useEffect(() => {
    loadData();
  }, [filter.campaign_id, filter.type]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Загружаем кампании
      const campRes = await fetch(`${API_URL}/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const campData = await campRes.json();
      if (campData.success) {
        setCampaigns(campData.data || []);
      }

      // Загружаем лиды (из commissions)
      let url = `${API_URL}/business/leads?`;
      if (filter.campaign_id) url += `campaign_id=${filter.campaign_id}&`;
      if (filter.type) url += `type=${filter.type}&`;

      const leadsRes = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const leadsData = await leadsRes.json();
      
      if (leadsData.success) {
        setLeads(leadsData.data || []);
        setStats(leadsData.stats || { total: 0, direct: 0, referral: 0 });
      }
    } catch (error) {
      console.error('Load leads error:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (leads.length === 0) return;

    const headers = ['Имя', 'Фамилия', 'Email', 'Телефон', 'Тип', 'Кампания', 'Промокод', 'Дата'];
    const rows = leads.map(l => [
      l.first_name || '',
      l.last_name || '',
      l.email || '',
      l.phone || '',
      l.type === 'direct' ? 'Прямой' : 'Реферал',
      l.campaign_name || '',
      l.promocode || '',
      formatDate(l.created_at)
    ]);

    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredLeads = leads.filter(l => {
    if (!filter.search) return true;
    const search = filter.search.toLowerCase();
    return (
      (l.first_name || '').toLowerCase().includes(search) ||
      (l.last_name || '').toLowerCase().includes(search) ||
      (l.email || '').toLowerCase().includes(search) ||
      (l.phone || '').includes(search)
    );
  });

  if (loading) {
    return <div className="bl-loading"><div className="bl-spinner"></div></div>;
  }

  return (
    <div className="bl-page">
      <div className="bl-header">
        <div className="bl-header-left">
          <h1>🎯 Лиды</h1>
          <p>Пользователи, обменявшие DAR на промокоды</p>
        </div>
        <button className="bl-btn bl-btn-primary" onClick={exportCSV} disabled={leads.length === 0}>
          📥 Экспорт CSV
        </button>
      </div>

      {/* Статистика */}
      <div className="bl-stats">
        <div className="bl-stat">
          <span className="bl-stat-num">{stats.total}</span>
          <span className="bl-stat-label">Всего лидов</span>
        </div>
        <div className="bl-stat">
          <span className="bl-stat-num bl-stat-green">{stats.direct}</span>
          <span className="bl-stat-label">Прямых</span>
        </div>
        <div className="bl-stat">
          <span className="bl-stat-num bl-stat-purple">{stats.referral}</span>
          <span className="bl-stat-label">Рефералов</span>
        </div>
        <div className="bl-stat">
          <span className="bl-stat-num bl-stat-blue">€{((stats.direct + stats.referral) * 20)}</span>
          <span className="bl-stat-label">Оплачено</span>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bl-filters">
        <select 
          value={filter.campaign_id} 
          onChange={(e) => setFilter({...filter, campaign_id: e.target.value})}
          className="bl-select"
        >
          <option value="">Все кампании</option>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <select 
          value={filter.type} 
          onChange={(e) => setFilter({...filter, type: e.target.value})}
          className="bl-select"
        >
          <option value="">Все типы</option>
          <option value="direct">Прямые</option>
          <option value="referral">Рефералы</option>
        </select>

        <input
          type="text"
          placeholder="🔍 Поиск по имени, email, телефону..."
          value={filter.search}
          onChange={(e) => setFilter({...filter, search: e.target.value})}
          className="bl-search"
        />
      </div>

      {/* Таблица лидов */}
      {filteredLeads.length === 0 ? (
        <div className="bl-empty">
          <div className="bl-empty-icon">🎯</div>
          <h3>Пока нет лидов</h3>
          <p>Лиды появятся когда пользователи обменяют DAR на промокоды</p>
        </div>
      ) : (
        <div className="bl-table-wrapper">
          <table className="bl-table">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Email</th>
                <th>Телефон</th>
                <th>Тип</th>
                <th>Кампания</th>
                <th>Промокод</th>
                <th>Дата</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead, i) => (
                <tr key={i}>
                  <td className="bl-name">
                    {lead.first_name} {lead.last_name}
                  </td>
                  <td>
                    <a href={`mailto:${lead.email}`}>{lead.email}</a>
                  </td>
                  <td>{lead.phone || '—'}</td>
                  <td>
                    <span className={`bl-badge bl-badge-${lead.type}`}>
                      {lead.type === 'direct' ? '🎁 Прямой' : '👥 Реферал'}
                    </span>
                  </td>
                  <td>{lead.campaign_name || '—'}</td>
                  <td className="bl-code">{lead.promocode || '—'}</td>
                  <td className="bl-date">{formatDate(lead.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BusinessLeads;
